import React, { useEffect, useState } from 'react'; // removed useState

//DATA
import {
	ContactOrganizationEntity,
	ContextEntity,
	LicenseEntities,
	LicenseServiceType,
	ServiceEntity
} from '../../store';

//UI
import {
	IonGrid,
	IonCard,
	IonCardContent,
	IonButton,
	IonContent,
	IonText,
	IonImg,
	IonSkeletonText
} from '@ionic/react';

import { ServiceNotFoundCard } from './../components/Service/ServiceNotFoundCard';
import { ApiAuthTypes } from '../../../app/store';
import { appApis } from '../../../app/api';
import { leadConfig as config } from '../../../config/lead';
import axios from 'axios';
import { CopyToClipboard } from '../../../app/ui/components/CopyToClipboard';

const ViewLicensesView: React.FC<any> = viewProps => {
	const [currentServiceId, setCurrentServiceId] = useState(
		viewProps.match?.params?.serviceId ?? ''
	);
	const [currentContextId, setCurrentContextId] = useState(
		viewProps.match?.params?.contextId ?? ''
	);
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState('');
	const [currentContact, setCurrentContact] = useState<
		ContactOrganizationEntity
	>();
	const [currentContext, setCurrentContext] = useState<ContextEntity>();
	const [currentService, setCurrentService] = useState<ServiceEntity>();
	const [currentLicenses, setCurrentLicenses] = useState<LicenseEntities>([]);

	const onError = (message: string) => {
		setHasError(true);
		setErrorMessage(message);
		setIsLoading(false);
	};

	const isGuid = (value: string): boolean =>
		/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
			value?.trim()
		);

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				if (
					currentServiceId &&
					currentContextId &&
					isGuid(currentServiceId) &&
					isGuid(currentContextId)
				) {
					// Ensure we have the context in the store
					const api = appApis[config.apiId];
					const token =
						api.auth.type == ApiAuthTypes.BearerToken ? api.auth.token : null;
					const baseUrl =
						'https://alpha-dev-api.conexsys.com/lead-admin/v3/api';
					if (!token) {
						onError('Missing user token');
						return;
					}
					const authHeaders = {
						headers: {
							Authorization: `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					};

					// Get the service
					const servicesRes = await axios.get(`${baseUrl}/services`, {
						params: {
							ids: [currentServiceId]
						},
						...authHeaders
					});

					// Get the context
					const contextsRes = await axios.get(`${baseUrl}/contexts`, {
						params: {
							ids: [currentContextId]
						},
						...authHeaders
					});

					// Check for errors
					if (servicesRes.status !== 200 || contextsRes.status !== 200) {
						onError('Failed to fetch service or context');
						return;
					}

					//Get licenses
					const licensesRes = await axios.get(`${baseUrl}/licenses`, {
						params: {
							serviceIds: [currentServiceId],
							contextIds: [currentContextId]
						},
						...authHeaders
					});

					// Check for errors
					if (licensesRes.status !== 200) {
						onError('Failed to fetch licenses');
						return;
					}

					// Get the contact info
					const contactRes = await axios.get(`${baseUrl}/contacts`, {
						params: {
							ids: [servicesRes.data[0].contactIds?.[0]]
						},
						...authHeaders
					});

					// Check for errors
					if (contactRes.status !== 200) {
						onError('Failed to fetch contact');
						return;
					}

					const contextData = contextsRes.data?.filter(
						(l: any) => l.i_?.status === 'active' && l.id === currentContextId
					)[0];
					const serviceData = servicesRes.data?.filter(
						(l: any) =>
							l.i_?.status === 'active' &&
							l.id === currentServiceId &&
							l.contextIds.includes(currentContextId)
					)[0];
					if (!serviceData) {
						onError('Service not found');
						return;
					}

					if (!contextData) {
						onError('Context not found');
						return;
					}
					const contactData = contactRes.data?.filter(
						(l: any) =>
							l.i_?.status === 'active' && l.id === serviceData.contactIds?.[0]
					)[0];
					if (!contactData) {
						onError('Contact not found');
						return;
					}
					const licensesData = licensesRes.data?.filter(
						(l: any) =>
							l.contextId === currentContextId &&
							l.serviceId === currentServiceId &&
							l.i_?.status === 'active'
					);
					if (!licensesData || licensesData.length === 0) {
						onError('Licenses not found');
						return;
					}

					// Set the data
					setCurrentService(serviceData);
					setCurrentContext(contextData);
					setCurrentContact(contactData);
					setCurrentLicenses(licensesData);
				} else {
					onError('Invalid identifiers supplied');
				}
				setIsLoading(false);
			} catch (e) {
				onError(`An error occurred`);
			}
		};
		fetchData();
	}, [currentServiceId, currentContextId]);

	// Prepare contexts and licenses early so we can base loading on real data

	const mobileLicenses = currentLicenses.filter(
		l => l.serviceType === LicenseServiceType.LeadApp
	);
	const apiLicenses = currentLicenses.filter(
		l => l.serviceType === LicenseServiceType.LeadApi
	);
	const opticonLicenses = currentLicenses.filter(
		l => l.serviceType === LicenseServiceType.Opticon
	);
	const sstLicenses = currentLicenses.filter(
		l => l.serviceType === LicenseServiceType.Sst
	);

	const opticonLicenseCount = opticonLicenses.reduce(
		(a, b) => a + b.quantity,
		0
	);
	const sstLicenseCount = sstLicenses.reduce((a, b) => a + b.quantity, 0);

	if (hasError) {
		return (
			<IonContent fullscreen>
				<IonGrid>
					<IonCard className="max-w-2xl mx-auto border border-gray-800 font-sans">
						<IonCardContent className="text-center">
							<IonImg
								src="https://my.conexsys.com/clients/ui/img/mail/clo-email-header.png"
								alt="Conexsys Header"
								className="mx-auto"
							/>
							<hr className="border-t-2 border-gray-200 my-5" />
							<h2 className="font-bold mb-4">Licenses could not be found</h2>
							<p className="text-gray-600">
								We were unable to load your licenses. Please refresh the page or
								contact our{' '}
								<a
									href="https://conexsys.com/lead-support/"
									target="_blank"
									className="text-blue-600"
								>
									Support Team
								</a>
								.
							</p>
						</IonCardContent>
					</IonCard>
				</IonGrid>
			</IonContent>
		);
	}

	if (isLoading) {
		return (
			<IonContent
				fullscreen
				className="bg-gradient-to-br from-gray-50 to-gray-100"
			>
				<IonGrid style={{ maxWidth: '700px', margin: '0 auto' }}>
					<IonCard className="border border-gray-800 font-sans">
						<IonCardContent className="text-center">
							<IonImg
								src="https://my.conexsys.com/clients/ui/img/mail/clo-email-header.png"
								alt="Conexsys Header"
								className="mx-auto"
							/>
							<hr className="border-t-2 border-gray-200 my-2" />
						</IonCardContent>
						<IonCardContent>
							<div className="flex flex-col items-center justify-center h-full px-2">
								<div className="w-full max-w-2xl bg-white rounded-xl">
									<div className="space-y-5">
										<div>
											<IonSkeletonText
												animated
												style={{ width: '55%', height: '18px' }}
											/>
											<IonSkeletonText
												animated
												style={{
													width: '35%',
													height: '12px',
													marginTop: '8px'
												}}
											/>
										</div>
										<div className="grid grid-cols-3 gap-4">
											<IonSkeletonText animated style={{ height: '70px' }} />
											<IonSkeletonText animated style={{ height: '70px' }} />
											<IonSkeletonText animated style={{ height: '70px' }} />
										</div>
										<div className="space-y-3">
											<IonSkeletonText
												animated
												style={{ width: '90%', height: '14px' }}
											/>
											<IonSkeletonText
												animated
												style={{ width: '82%', height: '14px' }}
											/>
											<IonSkeletonText
												animated
												style={{ width: '76%', height: '14px' }}
											/>
										</div>
									</div>
								</div>
							</div>
						</IonCardContent>
					</IonCard>
				</IonGrid>
			</IonContent>
		);
	}

	if (!currentService) return <ServiceNotFoundCard />;

	const MobileLicenseContent = () =>
		mobileLicenses.length > 0 ? (
			<>
				<hr className="border-t-2 border-gray-200 my-2" />
				<h1>MyLeads Mobile App</h1>
				<IonButton
					href="https://conx.is/la"
					target="_blank"
					color="primary"
					className="mt-4 w-64 no-print"
					style={{ backgroundColor: '#202ACB', borderColor: '#202ACB' }}
				>
					Download the app
				</IonButton>
				<IonText>
					<p className="mt-4">
						Instructions on how to setup your mobile lead scanning devices can
						be found in our{' '}
						<a
							href="https://leads.conexsys.com/assets/docs/CONEXSYS_MyLEADS%20Mobile%20App%20-%20Quick%20Start%20Guide.pdf"
							target="_blank"
							className="text-blue-600"
						>
							Quick Start Guide
						</a>{' '}
						or our{' '}
						<a
							href="https://youtu.be/Tb4BjV05qYY"
							target="_blank"
							className="text-blue-600"
						>
							Getting Started Video.
						</a>{' '}
					</p>
					<p className="mt-4">
						After you have created/activated your scanning account, you can scan
						the QR Code below to activate your mobile scanning device.
					</p>
				</IonText>
				<div className="mt-4">
					{mobileLicenses.map(license => (
						<div key={license.code} className="flex items-center mb-4">
							<div className="w-24 h-24 mr-4 flex items-center justify-center">
								<img
									src={`https://myconexsys.com/Home/GenerateQRCode?data=${license.code}`}
									alt={`QR Code for ${license.code}`}
									className="w-full h-full object-contain"
									loading="lazy"
								/>
							</div>
							<div className="text-left">
								<div className="flex items-center">
									<p className="font-bold mr-2">
										<strong>Activation Code: </strong>
									</p>
								</div>
								<p className="font-mono text-lg">
									<CopyToClipboard text={license.code}>
										{license.code}
									</CopyToClipboard>
								</p>
								<p className="font-bold">
									<br />
									<strong>Connect Up to {license.quantity} Devices</strong>
								</p>
							</div>
						</div>
					))}
				</div>
				<IonText>
					<p className="mt-4">
						<span className="font-bold">Please Note:</span> All onsite reps will
						need to create an account in order to activate their scanning
						devices and/or access the portal. The MyLEADS - Mobile App requires
						an internet connection to operate. Physical structures and/or signal
						disturbances can disrupt/weaken Wi-Fi and data connections. If your
						device does not have a connection, you can either purchase internet
						services through the event center (if offered) or operate the app
						offline. To do this, turn your phone on airplane mode. The app will
						only capture 6 digit codes but once you establish an internet
						connection, your information will be synced.
					</p>
				</IonText>
			</>
		) : null;

	const ApiLicenseContent = () =>
		apiLicenses.length > 0 ? (
			<>
				<hr className="border-t-2 border-gray-200 my-5" />
				<h1>MyLeads API Connection</h1>
				<IonText>
					<p className="mt-4">
						Instructions on how to use the MyLEADS by CONEXSYS API can be found
						at{' '}
						<a
							href="https://api.conexsys.com/lead/v2/docs/"
							target="_blank"
							className="text-blue-600"
						>
							https://api.conexsys.com/lead/v2/docs/
						</a>
						.<br />
						Test badges can be accessed by contacting our{' '}
						<a
							href="https://conexsys.com/lead-support/"
							target="_blank"
							className="text-blue-600"
						>
							Support Team
						</a>
						.
					</p>
				</IonText>
				<div className="mt-4">
					<p className="font-bold">API Access</p>
					{apiLicenses.map(license => (
						<div key={license.id} className="mb-4">
							<p>
								<span className="font-bold">License Id:</span> {license.id}
							</p>
							<p>
								<span className="font-bold">License Key:</span> {license.code}
							</p>
						</div>
					))}
				</div>
			</>
		) : null;

	const OpticonLicenseContent = () =>
		opticonLicenseCount > 0 ? (
			<>
				<hr className="border-t-2 border-gray-200 my-5" />
				<h1 className="text-xl font-bold">MyLeads Opticon</h1>
				<IonText>
					<p className="mt-4">
						Your handheld Opticon scanner devices have been reserved and will be
						available to collect onsite at the Lead Retrieval Service Desk. No
						additional steps are required at this time. You are able to add and
						manage your custom qualifiers from the MyLEADS by CONEXSYS Lead
						Portal anytime before and during the event.
					</p>
				</IonText>
				<div className="w-24 h-24 mr-4 flex items-center justify-center">
					<img
						src="https://leads.conexsys.com/assets/img/Opticon_9725.jpg"
						alt="Opticon Scanner"
						className="w-full h-full object-contain"
						loading="lazy"
					/>
					<p className="font-bold">Quantity: {opticonLicenseCount}</p>
				</div>
			</>
		) : null;

	const SstLicenseContent = () =>
		sstLicenseCount > 0 ? (
			<>
				<hr className="border-t-2 border-gray-200 my-5" />
				<h1 className="text-xl font-bold">MyLeads SST</h1>
				<IonText>
					<p className="mt-4">
						Your SST devices will be available at the Lead service desk onsite.
					</p>
				</IonText>
				<div className="flex items-center mt-4">
					<IonImg
						src="https://leads.conexsys.com/assets/img/bc500dc.jpg"
						alt="SST Device"
						className="w-36 h-36 mr-4"
					/>
					<p className="font-bold">Quantity: {sstLicenseCount}</p>
				</div>
			</>
		) : null;

	const isPostShow =
		currentContext && currentContext.end
			? new Date() > new Date(currentContext.end)
			: false;
	const eventName = currentContext?.name || '';

	return (
		<IonContent
			fullscreen
			className="bg-gradient-to-br from-gray-50 to-gray-100"
		>
			<style>
				{`@media print {
  .no-print, .no-print * {
    display: none !important;
  }
}`}
			</style>
			{currentContext && (
				<IonCard
					style={{ maxWidth: '700px', margin: '0 auto' }}
					className="border border-gray-800 font-sans"
				>
					<IonCardContent className="text-center">
						<IonImg
							src="https://my.conexsys.com/clients/ui/img/mail/clo-email-header.png"
							alt="Conexsys Header"
							className="mx-auto"
						/>
						<hr className="border-t-2 border-gray-200 my-1" />
					</IonCardContent>
					<IonCardContent className="pl-5 pr-5 pb-5">
						{isPostShow ? (
							<>
								<IonText>
									<p>
										{currentContext?.name || ''}
										<br />
										{currentContact?.contactFirstName || ''}{' '}
										{currentContact?.contactLastName || ''}
									</p>
									<p className="mt-4">
										Dear {currentContact?.contactFirstName || 'Lead Customer'},
									</p>
									<p className="mt-4">
										Thank you for using MyLEADS by CONEXSYS Services for your
										booth at {eventName}. Each badge you scanned during the
										event can be viewed in your Lead Portal.
									</p>
								</IonText>
								<IonButton
									href={`https://leads.conexsys.com/activate?serviceId=${currentService?.id}&contextId=${currentContext?.id}`}
									target="_blank"
									color="primary"
									className="mt-6 w-64"
									style={{
										backgroundColor: '#00a2eb',
										borderColor: '#00a2eb'
									}}
								>
									Click Here to Access Your Lead Portal
								</IonButton>
								<IonText>
									<p className="mt-4">
										Do not access this link on public computers...
									</p>
								</IonText>
							</>
						) : (
							<>
								<h3 className="text-xl font-bold">
									Your MyLEADS by CONEXSYS licenses for{' '}
									<strong>{eventName}</strong>
								</h3>
								<IonText>
									<p>
										<br />
										Welcome! These are your lead retrieval licenses for your
										upcoming event. To complete setup please review the
										instructions provided.
									</p>
									<br />
								</IonText>
								<MobileLicenseContent />
								<OpticonLicenseContent />
								<SstLicenseContent />
								<ApiLicenseContent />
							</>
						)}
						<hr className="border-t-2 border-gray-200 my-5" />
						<p className="text-center text-gray-400">
							If you are having any issues with your account, please don't
							hesitate to contact our{' '}
							<a
								href="https://conexsys.com/lead-support/"
								target="_blank"
								className="text-blue-600"
							>
								Support Team
							</a>
							.
						</p>
						<div className="text-center no-print">
							<IonButton
								target="_blank"
								color="primary"
								className="mt-6 w-64"
								style={{
									backgroundColor: '#00a2eb',
									borderColor: '#00a2eb'
								}}
								onClick={() => window.print()}
							>
								Print Page
							</IonButton>
						</div>
					</IonCardContent>
				</IonCard>
			)}
		</IonContent>
	);
};

export default ViewLicensesView;
